// Lot « image principale » (2026-10-01, corrections F1-F10) : helpers PURS
// pour désigner l'image principale d'un POI côté client. Ils doivent rester
// alignés sur le contrat du DTO backend (controllers/poi.controller.js) :
// - identité d'une image OCI existante = son checksum SHA-256 (64 hex
//   minuscules) ;
// - identité d'une image ajoutée dans la session = le contentKey calculé
//   par la file d'upload (même SHA-256 que le backend une fois publié) ;
// - image legacy sans média serveur = identité locale "legacy-<nom
//   normalisé>" STABLE PAR NOM, jamais envoyée dans mainImage (le backend
//   refuserait un non-checksum).
//
// F2/F4/F6/F8 : l'identité des images du formulaire vit dans un tableau
// parallèle `mainImageIds` maintenu par MarkerEditionForm, strictement
// aligné sur markerImage/markerImageName. AUCUNE heuristique positionnelle
// n'est plus appliquée ici : ce module ne fait que construire l'état
// INITIAL, comparer et trier.
//
// Ce module n'a AUCUNE dépendance : il est utilisé par les composants
// d'édition comme par la couche api (tri local après flush).

const MAIN_IMAGE_CHECKSUM = /^[0-9a-f]{64}$/;

export const isImageChecksum = (value) =>
  typeof value === "string" && MAIN_IMAGE_CHECKSUM.test(value);

// F1 (revue du 01/10) : les médias uploadés réels ne portent pas toujours
// le champ status — un média est actif à moins d'être explicitement en
// cours de suppression ("deleting").
const isActiveMedia = (entry) =>
  !!entry && entry.mediaType === "image" && entry.status !== "deleting";

// Comparaison tolérante alignée sur le repli legacy du DTO backend
// (normalizeMediaName) : casse, accents (NFD), espaces et ponctuation sont
// ignorés — la preview locale porte le nom NORMALISÉ par normalizeFileName
// tandis que le média publié porte l'originalName brut.
// Constat f14 (revue finale du 01/10) : EXPORTÉE — la couche api appliquait
// une copie privée (sameDisplayName/canonical) qui pouvait dériver du
// contrat de comparaison du domaine.
export const normalizeMediaName = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

// Identité legacy stable DÉRIVÉE DU NOM normalisé (F2) : la même entrée
// retrouve la même identité quelle que soit sa position dans la liste,
// contrairement à un index. Resserrement du 01/10 : le suffixe -2, -3…
// rend deux homonymes legacy DISTINCTS dès la construction initiale.
const legacyIdentityOfName = (name) => `legacy-${normalizeMediaName(name)}`;

// Identité serveur de repli pour un média SANS checksum : dérivée du nom
// normalisé, suffixée en cas d'homonymes. Elle n'est JAMAIS envoyée dans
// mainImage (pas un checksum) mais reste stable et distinguable.
const serverIdentityOfName = (name) => `server-${normalizeMediaName(name)}`;

// Attribue des identités uniques par nom de repli : chaque homonyme
// reçoit un suffixe croissant (-2, -3…) à partir du second.
const buildFallbackIdentities = (prefix, names) => {
  const counts = new Map();
  return (names || []).map((name) => {
    const base = prefix(name);
    const seen = (counts.get(base) || 0) + 1;
    counts.set(base, seen);
    return seen === 1 ? base : `${base}-${seen}`;
  });
};

/**
 * Image active (F1 : sans status = active) correspondant à une identité :
 * checksum serveur EXACT UNIQUEMENT. Une identité connue désigne toujours
 * un contenu précis (checksum, clé locale legacy-… ou server-…) : le
 * fallback par nom (revue finale du 01/10, point 4) est retiré — il
 * faisait trouver un média serveur à une identité « server-<nom> » de
 * repli, sans garantie que ce soit le bon homonyme.
 */
export const findActiveImageByIdentity = (media, identity) => {
  if (!identity) return undefined;
  const entries = Array.isArray(media) ? media : [];
  return entries.find(
    (entry) => isActiveMedia(entry) && entry.checksum === identity
  );
};

/**
 * F10 : le checksum proposé (contentKey d'un nouveau fichier) existe-t-il
 * déjà parmi les images serveur actives ? Vrai → ne pas ajouter un second
 * aperçu du même contenu.
 */
export const isChecksumAmongActiveImages = (media, checksum) => {
  if (!isImageChecksum(checksum)) return false;
  return (media || []).some(
    (entry) => isActiveMedia(entry) && entry.checksum === checksum
  );
};

// Nom d'affichage d'un média serveur (originalName, sinon fileName).
// Constat f19 (revue finale du 01/10) : EXPORTÉ — le formulaire et la
// couche api dupliquaient cette dérivation ; une seule source désormais.
export const mediaName = (media) =>
  media ? media.originalName || media.fileName : undefined;

/**
 * F2 + resserrement du 01/10 : état initial du tableau parallèle
 * mainImageIds, construit PAR SOURCE et dans l'ordre CONNU du formulaire —
 * AUCUN matching par nom pour les médias serveur :
 *
 * 1. `legacyNames` : entrées legacy (filtrées) en tête — identité
 *    "legacy-<nom normalisé>", suffixée (-2, -3…) si homonymes ;
 * 2. `serverMedia` : médias serveur image ACTIFS (status ≠ "deleting" et
 *    url PRÉSENTE — même contrat que le formulaire, constat f15 de la
 *    revue finale du 01/10 : le filtre est appliqué ICI, l'appelant peut
 *    passer marker.media brut) dans leur ordre — checksum de chacun PAR
 *    INDEX ; un média sans checksum reçoit une identité "server-<nom>"
 *    suffixée si homonymes (jamais envoyée).
 *
 * L'ordre produit est exactement celui de markerImageName initial
 * (legacy d'abord, puis médias serveur) : le tableau parallèle reste
 * strictement aligné entrée à entrée.
 *
 * Les images AJOUTÉES dans la session ne passent PAS par ici : leur entrée
 * est créée au moment de l'upload avec le contentKey exact retourné par
 * queuePoiMediaUpload (voir MarkerEditionForm.handleImageChange).
 */
export const buildInitialImageIds = ({ legacyNames, serverMedia }) => {
  const legacyIdentities = buildFallbackIdentities(
    legacyIdentityOfName,
    legacyNames
  );
  const serverImages = (serverMedia || []).filter(
    (entry) => isActiveMedia(entry) && entry.url
  );
  const serverNames = serverImages.map(mediaName);
  const serverFallbacks = buildFallbackIdentities(
    serverIdentityOfName,
    serverNames
  );
  const serverIdentities = serverImages.map((entry, index) =>
    isImageChecksum(entry.checksum) ? entry.checksum : serverFallbacks[index]
  );
  return [...legacyIdentities, ...serverIdentities];
};

/**
 * Détermine l'image principale à partir du tableau parallèle :
 * 1. choix existant encore présent dans ids ;
 * 2. repli legacy : mainResource comparé de façon tolérante aux noms ;
 * 3. sinon première image.
 * Sans aucune image : "" (le backend l'interprète comme un effacement).
 */
export const selectMainImage = ({ ids, names, mainImage, mainResource }) => {
  const identities = ids || [];
  if (identities.length === 0) return "";
  if (mainImage && identities.includes(mainImage)) return mainImage;
  if (typeof mainResource === "string" && mainResource !== "") {
    const wanted = normalizeMediaName(mainResource);
    if (wanted !== "") {
      const index = (names || []).findIndex(
        (name) => normalizeMediaName(name) === wanted
      );
      if (index >= 0 && identities[index]) return identities[index];
    }
  }
  return identities[0];
};

/**
 * F9 : suffixe visuellement les homonymes — le premier "a.png" reste
 * "a.png", les suivants deviennent "a.png (2)", "a.png (3)"… L'IDENTITÉ
 * (valeur du choix) n'est jamais modifiée.
 */
export const suffixDuplicateNames = (names) => {
  const counts = new Map();
  return (names || []).map((name) => {
    const label = String(name ?? "");
    const seen = (counts.get(label) || 0) + 1;
    counts.set(label, seen);
    return seen === 1 ? label : `${label} (${seen})`;
  });
};

/**
 * Options du sélecteur : dédupliquées par identité (deux entrées de même
 * contenu ne proposent qu'un choix), libellé = nom lisible suffixé en cas
 * d'homonymes (F9), valeur = identité stable.
 */
export const buildMainImageChoices = ({ ids, names }) => {
  const labels = suffixDuplicateNames(names);
  const seen = new Set();
  const choices = [];
  (ids || []).forEach((id, index) => {
    if (seen.has(id)) return;
    seen.add(id);
    choices.push({ id, label: labels[index] ?? String(id) });
  });
  return choices;
};

/**
 * Constat 1 (2e revue du 01/10) : à la sauvegarde, le tableau parallèle
 * est PERSISTÉ sur le marqueur local (mainImageIds, client-only) dans
 * l'ordre exact où le formulaire le reconstruira à la réouverture —
 * entrées des champs legacy APRÈS strip serveur (legacy + pending) PUIS
 * médias serveur actifs — pour rester aligné entrée à entrée avec le
 * initialImageName de la réouverture. Chaque nom est résolu vers
 * l'identité du formulaire par consommation exacte (première occurrence
 * non consommée). Si UN seul nom est introuvable (état incohérent),
 * retourne null : l'appelant ne persiste alors RIEN — la réouverture
 * retombera sur buildInitialImageIds plutôt qu'un tableau désaligné.
 *
 * Défaut élevé « réouverture » (revue finale du 01/10) : les médias
 * serveur passés par l'appelant doivent être TRIÉS PAR mainImage AVANT
 * la résolution — markerToUpdate.media est retrie au même moment et la
 * réouverture relit ce média trié ; un tableau persisté non trié
 * désalignerait l'entrée visée à la suppression (remove appelé sur le
 * mauvais média). mainImage absent/non checksum : ordre conservé.
 */
export const orderImageIdsForReopen = ({
  formNames,
  formIds,
  keptNames,
  serverMedia,
  mainImage,
}) => {
  const names = formNames || [];
  const ids = formIds || [];
  if (ids.length !== names.length) return null;
  const used = new Set();
  const idOfName = (name) => {
    const index = names.findIndex(
      (candidate, position) => candidate === name && !used.has(position)
    );
    if (index < 0 || typeof ids[index] === "undefined") return null;
    used.add(index);
    return ids[index];
  };
  const orderedIds = [];
  for (const name of keptNames || []) {
    const id = idOfName(name);
    if (id === null) return null;
    orderedIds.push(id);
  }
  // Miroir du tri de marker.media fait à la même sauvegarde : les médias
  // serveur ACTIFS (et affichables — url présente, même contrat que le
  // formulaire) sont d'abord triés par mainImage (principale en tête).
  const serverImages = orderMediaByMainImage(
    (serverMedia || []).filter((entry) => isActiveMedia(entry) && entry.url),
    mainImage
  );
  for (const entry of serverImages) {
    const id = idOfName(mediaName(entry));
    if (id === null) return null;
    orderedIds.push(id);
  }
  return orderedIds;
};

/**
 * Défaut élevé « réouverture » (revue finale du 01/10), point 3 : le
 * tableau client-only mainImageIds n'est RÉUTILISÉ à l'initialisation du
 * formulaire QUE s'il est ALIGNÉ avec l'état que le formulaire va
 * reconstruire : même longueur que [legacy filtrés + médias serveur
 * actifs] ET, pour la partie serveur, chaque id correspond au média
 * serveur du MÊME INDEX après la partie legacy (l'ordre affiché des
 * médias serveur est celui de marker.media, éventuellement trié par
 * mainImage à la sauvegarde précédente). Un tableau de longueurs
 * différentes, désordonné ou absent → rebuild complet par
 * buildInitialImageIds (les pièces pending retrouvent leurs contentKeys
 * exacts, les autres leurs identités stables).
 */
export const isReusableMainImageIds = ({
  mainImageIds,
  legacyNames,
  serverMedia,
}) => {
  const persisted = Array.isArray(mainImageIds) ? mainImageIds : null;
  if (!persisted) return false;
  const legacyCount = (legacyNames || []).filter(Boolean).length;
  const serverImages = (serverMedia || []).filter(
    (entry) => isActiveMedia(entry) && entry.url
  );
  if (persisted.length !== legacyCount + serverImages.length) return false;
  for (let index = 0; index < serverImages.length; index += 1) {
    if (persisted[legacyCount + index] !== serverImages[index].checksum) {
      return false;
    }
  }
  return true;
};

/**
 * Miroir local du tri du DTO backend : l'image active désignée par son
 * checksum (F1 : sans status = active) passe en tête, l'ordre relatif du
 * reste est conservé. Retourne le tableau D'ORIGINE (même référence) quand
 * rien ne bouge — déjà en tête, mainImage absent/invalide, sans
 * correspondance, média non image ou « deleting ». La base n'est jamais
 * réordonnée : lecture seule.
 */
export const orderMediaByMainImage = (media, mainImage) => {
  if (!isImageChecksum(mainImage)) return media;
  const entries = Array.isArray(media) ? media : [];
  const index = entries.findIndex(
    (entry) => isActiveMedia(entry) && entry.checksum === mainImage
  );
  if (index <= 0) return entries === media ? media : entries;
  return [
    entries[index],
    ...entries.slice(0, index),
    ...entries.slice(index + 1),
  ];
};
